import React, { useState, useEffect } from 'react';
import { itinerariesService } from '../../services/itinerariesService';
import './EditItineraryModal.css';

const EditItineraryModal = ({ isOpen, onClose, itinerary, onUpdate }) => {
  const [formData, setFormData] = useState({
    title: '',
    components: [],
    options: [],
    travel_start_date: '',
    travel_end_date: '',
    total_amount: 0,
    agent_notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('details');
  const [showComponentModal, setShowComponentModal] = useState(false);
  const [editingComponent, setEditingComponent] = useState(null);
  const [showOptionModal, setShowOptionModal] = useState(false);
  const [editingOption, setEditingOption] = useState(null);

  useEffect(() => {
    if (itinerary && isOpen) {
      // Parse components and options if they're strings
      let components = [];
      let options = [];
      
      try {
        if (itinerary.components) {
          components = typeof itinerary.components === 'string' 
            ? JSON.parse(itinerary.components) 
            : itinerary.components || [];
        }
      } catch (e) {
        console.error('Error parsing components:', e);
      }
      
      try {
        if (itinerary.options) {
          options = typeof itinerary.options === 'string' 
            ? JSON.parse(itinerary.options) 
            : itinerary.options || [];
        }
      } catch (e) {
        console.error('Error parsing options:', e);
      }

      setFormData({
        title: itinerary.title || '',
        components: Array.isArray(components) ? components : [],
        options: Array.isArray(options) ? options : [],
        travel_start_date: itinerary.travel_start_date ? 
          new Date(itinerary.travel_start_date).toISOString().split('T')[0] : '',
        travel_end_date: itinerary.travel_end_date ? 
          new Date(itinerary.travel_end_date).toISOString().split('T')[0] : '',
        total_amount: parseFloat(itinerary.total_amount) || 0,
        agent_notes: itinerary.agent_notes || ''
      });
    }
  }, [itinerary, isOpen]);

  const calculateTotal = () => {
    const componentTotal = formData.components.reduce((sum, comp) => sum + (parseFloat(comp.price) || 0), 0);
    const optionTotal = formData.options.reduce((sum, opt) => {
      const optComponents = opt.components || [];
      return sum + optComponents.reduce((optSum, comp) => optSum + (parseFloat(comp.price) || 0), 0);
    }, 0);
    return componentTotal + (formData.options.length > 0 ? optionTotal : 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const updateData = {
        title: formData.title,
        components: JSON.stringify(formData.components),
        options: JSON.stringify(formData.options),
        travel_start_date: formData.travel_start_date,
        travel_end_date: formData.travel_end_date,
        total_amount: calculateTotal(),
        agent_notes: formData.agent_notes
      };

      await itinerariesService.update(itinerary.id, updateData);
      onUpdate();
      onClose();
    } catch (err) {
      console.error('Error updating itinerary:', err);
      setError('Failed to update itinerary. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddComponent = () => {
    setEditingComponent(null);
    setShowComponentModal(true);
  };

  const handleEditComponent = (component, index) => {
    setEditingComponent({ ...component, index });
    setShowComponentModal(true);
  };

  const handleSaveComponent = (componentData) => {
    const newComponents = [...formData.components];
    if (editingComponent && editingComponent.index !== undefined) {
      newComponents[editingComponent.index] = componentData;
    } else {
      newComponents.push(componentData);
    }
    setFormData({ ...formData, components: newComponents });
    setShowComponentModal(false);
  };

  const handleDeleteComponent = (index) => {
    const newComponents = formData.components.filter((_, i) => i !== index);
    setFormData({ ...formData, components: newComponents });
  };

  const handleAddOption = () => {
    setEditingOption(null);
    setShowOptionModal(true);
  };

  const handleEditOption = (option, index) => {
    setEditingOption({ ...option, index });
    setShowOptionModal(true);
  };

  const handleSaveOption = (optionData) => {
    const newOptions = [...formData.options];
    if (editingOption && editingOption.index !== undefined) {
      newOptions[editingOption.index] = optionData;
    } else {
      newOptions.push(optionData);
    }
    setFormData({ ...formData, options: newOptions });
    setShowOptionModal(false);
  };

  const handleDeleteOption = (index) => {
    const newOptions = formData.options.filter((_, i) => i !== index);
    setFormData({ ...formData, options: newOptions });
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content edit-itinerary-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit Itinerary Details</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="modal-tabs">
          <button 
            className={`tab-button ${activeTab === 'details' ? 'active' : ''}`}
            onClick={() => setActiveTab('details')}
          >
            Details
          </button>
          <button 
            className={`tab-button ${activeTab === 'components' ? 'active' : ''}`}
            onClick={() => setActiveTab('components')}
          >
            Components ({formData.components.length})
          </button>
          <button 
            className={`tab-button ${activeTab === 'options' ? 'active' : ''}`}
            onClick={() => setActiveTab('options')}
          >
            Options ({formData.options.length})
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="edit-itinerary-form">
          {activeTab === 'details' && (
            <div className="tab-content">
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="form-input"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Travel Start Date</label>
                  <input
                    type="date"
                    value={formData.travel_start_date}
                    onChange={(e) => setFormData({ ...formData, travel_start_date: e.target.value })}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>Travel End Date</label>
                  <input
                    type="date"
                    value={formData.travel_end_date}
                    onChange={(e) => setFormData({ ...formData, travel_end_date: e.target.value })}
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Agent Notes</label>
                <textarea
                  value={formData.agent_notes}
                  onChange={(e) => setFormData({ ...formData, agent_notes: e.target.value })}
                  rows="4"
                  className="form-input"
                  placeholder="Internal notes about this itinerary..."
                />
              </div>

              <div className="total-amount">
                <h3>Total Amount: ${calculateTotal().toFixed(2)}</h3>
              </div>
            </div>
          )}

          {activeTab === 'components' && (
            <div className="tab-content">
              <div className="components-header">
                <h3>Travel Components</h3>
                <button type="button" className="btn btn-secondary" onClick={handleAddComponent}>
                  Add Component
                </button>
              </div>

              <div className="components-list">
                {formData.components.map((component, index) => (
                  <div key={index} className="component-item">
                    <div className="component-info">
                      <h4>{component.type} - {component.title}</h4>
                      <p>${parseFloat(component.price || 0).toFixed(2)}</p>
                    </div>
                    <div className="component-actions">
                      <button type="button" className="btn-icon" onClick={() => handleEditComponent(component, index)}>
                        ✏️
                      </button>
                      <button type="button" className="btn-icon" onClick={() => handleDeleteComponent(index)}>
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
                {formData.components.length === 0 && (
                  <p className="no-items">No components added yet.</p>
                )}
              </div>
            </div>
          )}

          {activeTab === 'options' && (
            <div className="tab-content">
              <div className="options-header">
                <h3>Package Options</h3>
                <button type="button" className="btn btn-secondary" onClick={handleAddOption}>
                  Add Option
                </button>
              </div>

              <div className="options-list">
                {formData.options.map((option, index) => (
                  <div key={index} className="option-item">
                    <div className="option-info">
                      <h4>Option {String.fromCharCode(65 + index)}: {option.name}</h4>
                      <p>{option.description}</p>
                      <p className="option-total">
                        Total: ${(option.components || []).reduce((sum, comp) => sum + (parseFloat(comp.price) || 0), 0).toFixed(2)}
                      </p>
                    </div>
                    <div className="option-actions">
                      <button type="button" className="btn-icon" onClick={() => handleEditOption(option, index)}>
                        ✏️
                      </button>
                      <button type="button" className="btn-icon" onClick={() => handleDeleteOption(index)}>
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
                {formData.options.length === 0 && (
                  <p className="no-items">No options added yet.</p>
                )}
              </div>
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>

        {/* Component Modal */}
        {showComponentModal && (
          <ComponentModal
            isOpen={showComponentModal}
            onClose={() => setShowComponentModal(false)}
            onSave={handleSaveComponent}
            component={editingComponent}
          />
        )}

        {/* Option Modal */}
        {showOptionModal && (
          <OptionModal
            isOpen={showOptionModal}
            onClose={() => setShowOptionModal(false)}
            onSave={handleSaveOption}
            option={editingOption}
          />
        )}
      </div>
    </div>
  );
};

// Simple Component Modal (you can enhance this based on your existing component modals)
const ComponentModal = ({ isOpen, onClose, onSave, component }) => {
  const [formData, setFormData] = useState({
    type: component?.type || 'Hotel',
    title: component?.title || '',
    description: component?.description || '',
    price: component?.price || 0
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content component-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{component ? 'Edit Component' : 'Add Component'}</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Type</label>
            <select 
              value={formData.type} 
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              className="form-input"
            >
              <option value="Flight">Flight</option>
              <option value="Hotel">Hotel</option>
              <option value="Car Rental">Car Rental</option>
              <option value="Cruise">Cruise</option>
              <option value="Restaurant">Restaurant</option>
              <option value="Package Deal">Package Deal</option>
              <option value="Travel Insurance">Travel Insurance</option>
              <option value="General Item">General Item</option>
            </select>
          </div>
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="3"
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label>Price *</label>
            <input
              type="number"
              step="0.01"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: e.target.value })}
              required
              className="form-input"
            />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Simple Option Modal
const OptionModal = ({ isOpen, onClose, onSave, option }) => {
  const [formData, setFormData] = useState({
    name: option?.name || '',
    description: option?.description || '',
    components: option?.components || []
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content option-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{option ? 'Edit Option' : 'Add Option'}</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Option Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="form-input"
              placeholder="e.g., Budget Package, Luxury Package"
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="3"
              className="form-input"
              placeholder="Describe what makes this option unique..."
            />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditItineraryModal;
